import { useContext, useRef } from 'react';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { v4 as uuidv4 } from 'uuid';

import { ConfigContext } from 'context/config';

import { useAspenLogger } from 'hooks/useAspenLogger';

import {
  AspenSocketMsg,
  CommandPayload,
  GetAssetsPayload,
  GetAssetsRxMsg,
  GetVideoPayload,
  QueryStatesPayload,
  UpdateSettingsPayload,
} from 'types/aspen/socketMessages';
import { AppLeaf } from 'types/common/leaf';
import { initializeAspenSocketsRequest, sendRequest, RequestOptions } from 'modules/aspenSocketsRequest';

interface AspenSocketReturn {
  connection: ReconnectingWebSocket;
  getLeaves: () => void;
  sendCommand: (params: { leaf: AppLeaf; command: CommandPayload }) => void;
  updateSettings: (params: { leaf: AppLeaf; settings: UpdateSettingsPayload }) => void;
  queryStates: (params: { payload: QueryStatesPayload }) => void;
  getStates: (params: Array<{ leaf?: AppLeaf; stateIds: Array<string> }>) => void;
  deleteStates: (params: Array<{ leaf?: AppLeaf; stateIds: Array<string> }>) => void;
  getVideo: (params: { payload: GetVideoPayload }) => void;
  getAssets: (params: { payload: GetAssetsPayload }) => Promise<GetAssetsRxMsg>;
  // Generic request method from module
  sendRequest: <TResponse, TPayload = unknown>(
    type: string,
    responseType: string,
    payload: TPayload,
    options?: RequestOptions,
  ) => Promise<{ response: TResponse; requestId: string }>;
  subscribe: (listener: (event: MessageEvent) => void) => void;
  unsubscribe: () => void;
}

let connection: ReconnectingWebSocket;

export const useAspenSocket = (): AspenSocketReturn => {
  const eventListeners = useRef<Array<(event: MessageEvent) => void>>([]);

  const { endpoints } = useContext(ConfigContext);
  const aspenSocketsUrl = `${endpoints['aspen_sockets.ws']}/v1/socket`;

  const { logAspenEvent } = useAspenLogger();

  if (!connection) {
    connection = new ReconnectingWebSocket(aspenSocketsUrl, [], {
      maxReconnectionDelay: 15000,
      minReconnectionDelay: 1500,
      reconnectionDelayGrowFactor: 2,
      minUptime: 4000,
      connectionTimeout: 2000,
      maxRetries: 5,
    });

    // Initialize the request module with the connection and logger
    initializeAspenSocketsRequest(connection, { logAspenEvent });
  }

  const getLeaves: AspenSocketReturn['getLeaves'] = () => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'get_leaves',
    };
    connection.send(JSON.stringify(message));
  };

  const sendCommand: AspenSocketReturn['sendCommand'] = ({ leaf, command }) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'send_command',
      payload: {
        leafId: leaf.id,
        leafType: leaf.type,
        command,
      },
    };
    connection.send(JSON.stringify(message));
  };

  const updateSettings: AspenSocketReturn['updateSettings'] = ({ leaf, settings }) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'update_settings',
      payload: {
        leafId: leaf.id,
        leafType: leaf.type,
        settings,
      },
    };
    connection.send(JSON.stringify(message));
  };

  const queryStates: AspenSocketReturn['queryStates'] = ({ payload }) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'query_states',
      payload,
    };

    // Log the query event
    logAspenEvent({
      log_id: 'iot.web.event.query.request',
      payload: {
        request_id: message.id,
        query_type: message.type,
        query_payload: payload,
      },
    });

    connection.send(JSON.stringify(message));
  };

  const getStates: AspenSocketReturn['getStates'] = (payload) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'get_states',
      payload,
    };

    // Log the query event
    logAspenEvent({
      log_id: 'iot.web.event.query.request',
      payload: {
        request_id: message.id,
        query_type: message.type,
        query_payload: message.payload,
      },
    });

    connection.send(JSON.stringify(message));
  };

  const deleteStates: AspenSocketReturn['deleteStates'] = (payload) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'delete_states',
      payload,
    };
    connection.send(JSON.stringify(message));
  };

  const getVideo: AspenSocketReturn['getVideo'] = ({ payload }) => {
    const message: AspenSocketMsg = {
      id: uuidv4(),
      type: 'get_video',
      payload,
    };

    // Log the video request event
    logAspenEvent({
      log_id: 'iot.web.event.video.request',
      payload: {
        request_id: message.id,
        device_id: payload.leafId,
        event_id: payload.stateId,
      },
    });

    connection.send(JSON.stringify(message));
  };

  const getAssets: AspenSocketReturn['getAssets'] = ({ payload }) =>
    new Promise((resolve, reject) => {
      const message: AspenSocketMsg = {
        id: uuidv4(),
        type: 'get_assets',
        payload,
      };

      const listener = (event: MessageEvent) => {
        const response: AspenSocketMsg = JSON.parse(event.data);
        if (response.id === message.id && response.type === 'assets') {
          connection.removeEventListener('message', listener);
          if (response.error) {
            reject(new Error(response.error));
          } else {
            resolve(response as GetAssetsRxMsg);
          }
        }
      };

      connection.addEventListener('message', listener);

      try {
        connection.send(JSON.stringify(message));
      } catch (error) {
        connection.removeEventListener('message', listener);
        reject(error);
        return;
      }

      // Timeout after 10 seconds
      setTimeout(() => {
        connection.removeEventListener('message', listener);
        reject(new Error('Request timeout'));
      }, 10000);
    });

  const subscribe: AspenSocketReturn['subscribe'] = (listener) => {
    connection.addEventListener('message', listener);
    eventListeners.current.push(listener);
  };

  const unsubscribe: AspenSocketReturn['unsubscribe'] = () => {
    eventListeners.current.forEach((listener) => connection.removeEventListener('message', listener));
  };

  return {
    connection,
    getLeaves,
    sendCommand,
    updateSettings,
    queryStates,
    getStates,
    deleteStates,
    getVideo,
    getAssets,
    sendRequest,
    subscribe,
    unsubscribe,
  };
};
